import React from "react";
import ReactTooltip from "react-tooltip";
import { t } from "ttag";

import { IFormUUID, IProductUUID } from "../../../models/nominals";
import {
    IReviewsProduct,
    IReviewsProductVariantChoices,
} from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import {
    NOT_SURE_VARIANT_CHOICE_SLUG,
    WriteReviewFormMode,
} from "../constants";
import { defaultState } from "../defaults";
import { IWriteReviewFormState } from "../models";
import { setWriteReviewFormMode } from "../reducers";
import {
    getProductByUUID,
    getWriteReviewFormSelectedVariant,
    getWriteReviewFormState,
} from "../selectors";

const getIsSelected = (formState: IWriteReviewFormState | null): boolean => {
    return formState?.mode !== WriteReviewFormMode.CLOSED;
};

const handleKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    isKeyDown: boolean,
    setIsKeyDown: (value: boolean) => void,
): void => {
    switch (event.key) {
        case "Escape":
            ReactTooltip.hide();
            break;
        case "Enter":
            if (!isKeyDown) {
                setIsKeyDown(true);
                return;
            }
            ReactTooltip.hide();
            break;
    }
};

export const MultiProductWriteReviewFormProductHeading = (props: {
    productUUID: IProductUUID;
    formUUID: IFormUUID;
    side?: string;
}): React.ReactNode => {
    const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
    const [isKeyDown, setIsKeyDown] = React.useState(false);

    const state = useAppSelector(
        (rootState) => rootState.reviews || defaultState,
    );
    const dispatch = useAppDispatch();

    const product: IReviewsProduct | null = getProductByUUID(state, props);
    const formState: IWriteReviewFormState | null = getWriteReviewFormState(
        state,
        props,
    );
    const selectedVariant: IReviewsProductVariantChoices =
        getWriteReviewFormSelectedVariant(state, props);

    const isSelected = getIsSelected(formState);

    const onCheckboxChange = () => {
        const newMode = isSelected
            ? WriteReviewFormMode.CLOSED
            : WriteReviewFormMode.FORM_OPEN;
        dispatch(
            setWriteReviewFormMode({
                formUUID: props.formUUID,
                mode: newMode,
            }),
        );
    };

    const updateTooltipStatus = () => {
        setIsTooltipOpen((prevState) => !prevState);
    };

    const keyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
        handleKeyDown(event, isKeyDown, setIsKeyDown);
    };

    if (!product || !formState) {
        return null;
    }

    if (formState?.mode === WriteReviewFormMode.THANK_YOU_OPEN) {
        return (
            <div key={`${product.uuid}`}>
                <label className="wrf-rating__title">
                    <input
                        type="checkbox"
                        name="multi-product-write-review-form-selected-products"
                        value={`${product.uuid}`}
                        disabled={true}
                        checked={isSelected}
                    />
                    {product.name}
                    {selectedVariant &&
                    selectedVariant !== NOT_SURE_VARIANT_CHOICE_SLUG
                        ? `: ${selectedVariant.display_name}`
                        : ""}
                </label>
            </div>
        );
    }

    const tipId = "multi-product-write-review__side-tooltip";
    return (
        <div key={`${product.uuid}`}>
            <label className="wrf-rating__title">
                <input
                    type="checkbox"
                    name="multi-product-write-review-form-selected-products"
                    value={`${product.uuid}`}
                    checked={isSelected}
                    onChange={onCheckboxChange}
                />
                {product.name}
                {props.side && ` Split King - Side ${props.side}`}
                {selectedVariant &&
                selectedVariant !== NOT_SURE_VARIANT_CHOICE_SLUG
                    ? `: ${selectedVariant.display_name}`
                    : ""}
            </label>
            {props.side && (
                <div>
                    <a
                        aria-describedby={tipId}
                        aria-expanded={isTooltipOpen}
                        className="multi-product-review__header-cart-ref-icon-container"
                        data-delay-update={100}
                        data-iscapture="true"
                        data-tip={true}
                        data-for={tipId}
                        data-delay-hide={200}
                        title={t`What does Side One and Side Two mean?`}
                        onKeyDown={keyDownHandler}
                        onMouseOver={() => {
                            setIsKeyDown(false);
                        }}
                    >
                        {t`What does Side One and Side Two mean`}
                    </a>
                    <ReactTooltip
                        afterHide={updateTooltipStatus}
                        afterShow={updateTooltipStatus}
                        aria-label={t`What does Side One and Side Two mean?`}
                        id={tipId}
                        class="tooltip"
                        event={"click"}
                        eventOff="scroll mousewheel mouseleave mouseout blur"
                        role="tooltip"
                    >
                        {t`You purchased a Split King Mattress with two different mattress feels.`}
                        <br />
                        {t`Please review each side of the mattress individually.`}
                    </ReactTooltip>
                </div>
            )}
        </div>
    );
};
